import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Pencil, Printer, Search, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

/* ------------------------------------------------------------------ */
/* Register definitions — field lists mirror the official TBS forms    */
/* ------------------------------------------------------------------ */

const REGISTERS = {
  complaints: {
    entity: 'complaints',
    title: 'Customer Complaints',
    docNo: 'QP-CC-01',
    formTitle: 'CUSTOMER COMPLAINT FORM',
    refField: 'complaint_ref',
    listColumns: [
      ['complaint_ref', 'Ref. No.'], ['date_received', 'Date'], ['customer_name', 'Customer'],
      ['product_name', 'Product'], ['batch_number', 'Batch'], ['severity', 'Severity'], ['status', 'Status'],
    ],
    sections: [
      { title: null, fields: [
        ['date_received', 'Date Received', 'date'], ['received_by', 'Received By', 'text'],
        ['received_via', 'Received Via', 'select', ['phone', 'email', 'in-person', 'SMS', 'distributor', 'TBS/regulatory']],
      ]},
      { title: 'Customer Details', fields: [
        ['customer_name', 'Customer Name', 'text'], ['customer_contact', 'Phone/Email', 'text'],
        ['customer_address', 'Address / Location', 'text'],
      ]},
      { title: 'Product / Complaint Details', fields: [
        ['product_name', 'Product Name', 'text'], ['batch_number', 'Batch/Lot No.', 'text'],
        ['production_date', 'Production Date', 'date'], ['quantity_involved', 'Quantity Involved', 'text'],
        ['category', 'Complaint Category', 'select', ['product quality/defect', 'packaging', 'quantity/short-weight', 'labelling', 'delivery', 'other']],
        ['severity', 'Severity', 'select', ['Critical', 'Major', 'Minor']],
      ]},
      { title: 'Description of Complaint', fields: [['description', 'Description', 'textarea']] },
      { title: 'Investigation Findings / Root Cause', fields: [['findings', 'Findings', 'textarea']] },
      { title: 'Corrective Action Taken', fields: [['corrective_action', 'Corrective Action', 'textarea']] },
      { title: null, fields: [
        ['investigated_by', 'Investigated By', 'text'], ['investigated_date', 'Investigation Date', 'date'],
        ['customer_informed_on', 'Customer Informed On', 'date'], ['response_given', 'Response Given', 'text'],
        ['closed_by', 'Closed By', 'text'], ['date_closed', 'Date Closed', 'date'],
        ['status', 'Status', 'select', ['open', 'under-investigation', 'closed']],
      ]},
    ],
  },
  recalls: {
    entity: 'recalls',
    title: 'Product Recalls',
    docNo: 'QF-TR-02',
    formTitle: 'PRODUCT RECALL RECORD',
    refField: 'recall_ref',
    listColumns: [
      ['recall_ref', 'Ref. No.'], ['date_initiated', 'Date'], ['product_name', 'Product'],
      ['batch_numbers', 'Batch(es)'], ['recall_class', 'Class'], ['status', 'Status'],
    ],
    sections: [
      { title: null, fields: [
        ['date_initiated', 'Date Initiated', 'date'], ['product_name', 'Product Name', 'text'],
        ['batch_numbers', 'Batch/Lot No.(s)', 'text'],
        ['recall_class', 'Recall Class', 'select', ['I', 'II', 'III']],
        ['quantity_produced', 'Quantity Produced', 'text'], ['quantity_retrieved', 'Quantity Retrieved', 'text'],
      ]},
      { title: 'Reason for Recall', fields: [['reason', 'Reason', 'textarea']] },
      { title: 'Customers/Locations Notified', fields: [['customers_notified', 'Customers Notified', 'textarea']] },
      { title: 'Disposition of Recalled Product', fields: [['disposition', 'Disposition', 'textarea']] },
      { title: 'Root Cause / Corrective Action', fields: [['root_cause', 'Root Cause / Corrective Action', 'textarea']] },
      { title: null, fields: [
        ['prepared_by', 'Prepared By', 'text'], ['approved_by', 'Approved By', 'text'],
        ['status', 'Status', 'select', ['open', 'in-progress', 'closed']],
      ]},
    ],
  },
  'maintenance-reports': {
    entity: 'maintenance-reports',
    title: 'Maintenance Reports',
    docNo: 'QF-MT-02',
    formTitle: 'MAINTENANCE REPORT FORM',
    refField: 'report_ref',
    listColumns: [
      ['report_ref', 'Ref. No.'], ['date', 'Date'], ['equipment_name', 'Equipment'],
      ['maintenance_type', 'Type'], ['equipment_status', 'Status After Work'], ['status', 'Status'],
    ],
    sections: [
      { title: null, fields: [
        ['equipment_name', 'Equipment Name/ID', 'text'], ['location', 'Location', 'text'],
        ['date', 'Date', 'date'],
        ['maintenance_type', 'Type', 'select', ['Preventive', 'Breakdown']],
        ['start_time', 'Start Time', 'time'], ['end_time', 'End Time / Downtime', 'text'],
      ]},
      { title: 'Work Performed / Fault Description', fields: [['work_performed', 'Work Performed', 'textarea']] },
      { title: 'Spare Parts / Materials Used', fields: [['spare_parts', 'Spare Parts', 'textarea']] },
      { title: null, fields: [
        ['equipment_status', 'Equipment Status After Work', 'select', ['Returned to service', 'Out of service']],
        ['carried_out_by', 'Carried Out By', 'text'], ['verified_by', 'Verified By (Supervisor)', 'text'],
        ['next_maintenance_date', 'Next Scheduled Maintenance Date', 'date'],
        ['status', 'Status', 'select', ['open', 'closed']],
      ]},
    ],
  },
  'calibration-records': {
    entity: 'calibration-records',
    title: 'Calibration Records',
    docNo: 'QF-CL-02',
    formTitle: 'CALIBRATION RECORD FORM',
    refField: 'record_ref',
    listColumns: [
      ['record_ref', 'Ref. No.'], ['calibration_date', 'Date'], ['instrument_name', 'Instrument'],
      ['overall_result', 'Result'], ['next_due_date', 'Next Due'], ['status', 'Status'],
    ],
    sections: [
      { title: null, fields: [
        ['instrument_name', 'Instrument Name/ID', 'text'], ['location', 'Location/Department', 'text'],
        ['calibration_date', 'Calibration Date', 'date'], ['next_due_date', 'Next Due Date', 'date'],
        ['calibrated_by', 'Calibrated By / Laboratory', 'text'],
        ['reference_standard', 'Reference Standard Used (with cert. no.)', 'text'],
      ]},
      { title: 'Readings / Results', fields: [['readings', 'Readings', 'readings']] },
      { title: null, fields: [
        ['overall_result', 'Overall Result', 'select', ['Pass', 'Fail']],
        ['action_if_failed', 'Action If Failed', 'text'],
        ['verified_by', 'Calibrated/Verified By', 'text'], ['approved_by', 'Reviewed/Approved By', 'text'],
        ['status', 'Status', 'select', ['open', 'closed']],
      ]},
    ],
  },
  'rejected-materials': {
    entity: 'rejected-materials',
    title: 'Rejected Materials',
    docNo: 'QF-RJ-02',
    formTitle: 'REJECTED MATERIAL REPORT',
    refField: 'report_ref',
    listColumns: [
      ['report_ref', 'Ref. No.'], ['date', 'Date'], ['material_name', 'Material/Product'],
      ['batch_number', 'Batch'], ['source', 'Source'], ['disposition', 'Disposition'], ['status', 'Status'],
    ],
    sections: [
      { title: null, fields: [
        ['date', 'Date', 'date'], ['material_name', 'Material/Product Name', 'text'],
        ['batch_number', 'Batch/Lot No.', 'text'],
        ['source', 'Source', 'select', ['Incoming Raw Material', 'In-Process', 'Finished Goods', 'Customer Return']],
        ['quantity_rejected', 'Quantity Rejected', 'text'], ['supplier', 'Supplier (if raw material)', 'text'],
        ['identified_by', 'Identified By', 'text'], ['quarantine_location', 'Quarantine Location', 'text'],
      ]},
      { title: 'Reason for Rejection / Non-Conformance Description', fields: [['reason', 'Reason', 'textarea']] },
      { title: 'Evaluation Findings', fields: [['evaluation', 'Evaluation', 'textarea']] },
      { title: null, fields: [
        ['disposition', 'Disposition', 'select', ['Rework', 'Return to Supplier', 'Downgrade', 'Destroy']],
        ['disposition_authorized_by', 'Disposition Authorized By', 'text'], ['disposition_date', 'Authorization Date', 'date'],
        ['witnessed_by', 'Disposal/Return Witnessed By', 'text'], ['witnessed_date', 'Witness Date', 'date'],
        ['status', 'Status', 'select', ['open', 'closed']],
      ]},
    ],
  },
  'corrective-actions': {
    entity: 'corrective-actions',
    title: 'Corrective Actions (CAR)',
    docNo: 'QF-RJ-03',
    formTitle: 'CORRECTIVE ACTION RECORD',
    refField: 'car_ref',
    listColumns: [
      ['car_ref', 'CAR Ref.'], ['date_raised', 'Date Raised'], ['linked_report_ref', 'Linked Report'],
      ['responsible_person', 'Responsible'], ['target_date', 'Target Date'], ['status', 'Status'],
    ],
    sections: [
      { title: null, fields: [
        ['linked_report_ref', 'Linked Rejected Material Report No.', 'text'],
        ['date_raised', 'Date Raised', 'date'], ['raised_by', 'Raised By', 'text'],
      ]},
      { title: 'Root Cause Analysis', fields: [['root_cause', 'Root Cause', 'textarea']] },
      { title: 'Corrective Action(s)', fields: [['corrective_actions', 'Corrective Action(s)', 'textarea']] },
      { title: null, fields: [
        ['responsible_person', 'Responsible Person', 'text'], ['target_date', 'Target Completion Date', 'date'],
      ]},
      { title: 'Verification of Effectiveness', fields: [['verification', 'Verification', 'textarea']] },
      { title: null, fields: [
        ['verified_by', 'Verified By', 'text'], ['date_closed', 'Date Closed', 'date'],
        ['approved_by', 'Approved By', 'text'],
        ['status', 'Status', 'select', ['open', 'closed']],
      ]},
    ],
  },
};

/* ------------------------------------------------------------------ */
/* Print — reproduces the official form layout in a new window         */
/* ------------------------------------------------------------------ */

let __logoDataUrl = null;
async function getLogoDataUrl() {
  if (__logoDataUrl) return __logoDataUrl;
  try {
    const res = await fetch('/himan-logo.png');
    const blob = await res.blob();
    __logoDataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch {
    __logoDataUrl = `${window.location.origin}/himan-logo.png`;
  }
  return __logoDataUrl;
}

async function printRecord(cfg, record) {
  // Open synchronously so the popup blocker sees the user gesture
  const win = window.open('', '_blank');
  if (!win) { toast.error('Popup blocked — allow popups to print forms'); return; }
  const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const fieldRow = (label, value) =>
    `<tr><td class="lbl">${esc(label)}:</td><td class="val">${esc(value)}</td></tr>`;

  let body = '';
  for (const section of cfg.sections) {
    const pairs = [];
    let blocks = '';
    for (const [key, label, type] of section.fields) {
      if (type === 'textarea') {
        blocks += `<div class="box">${esc(record[key]) || '&nbsp;'}</div>`;
      } else if (type === 'readings') {
        const rows = (record.readings || []).map(r =>
          `<tr><td>${esc(r.test_point)}</td><td>${esc(r.standard_value)}</td><td>${esc(r.instrument_reading)}</td><td>${esc(r.within_tolerance)}</td></tr>`
        ).join('') || '<tr><td colspan="4">&nbsp;</td></tr>';
        blocks += `<table class="grid"><tr><th>Test Point / Reading No.</th><th>Standard Value</th><th>Instrument Reading</th><th>Within Tolerance? (Y/N)</th></tr>${rows}</table>`;
      } else {
        pairs.push(fieldRow(label, record[key]));
      }
    }
    if (section.title) body += `<h3>${esc(section.title)}</h3>`;
    if (pairs.length) body += `<table class="fields">${pairs.join('')}</table>`;
    body += blocks;
  }

  const refValue = record[cfg.refField] || '';
  const html = `<!DOCTYPE html><html><head><title>${esc(refValue)} — ${esc(cfg.formTitle)}</title>
  <style>
    body { font-family: 'Times New Roman', serif; margin: 40px auto; max-width: 720px; color: #000; }
    .company { text-align: center; }
    .company h1 { font-size: 18px; margin: 0; }
    .company p { font-style: italic; font-size: 11px; margin: 2px 0 14px; }
    h2 { text-align: center; font-size: 14px; margin: 10px 0 16px; text-decoration: none; }
    h3 { color: #2e5b97; font-size: 12px; font-weight: normal; margin: 14px 0 4px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 8px; font-size: 11px; }
    .doc td, .doc th { border: 1px solid #999; padding: 3px 6px; text-align: left; }
    .doc th { background: #dce6f1; }
    .fields td { border: 1px solid #999; padding: 4px 6px; }
    .fields .lbl { background: #dce6f1; font-weight: bold; width: 32%; }
    .grid th, .grid td { border: 1px solid #999; padding: 4px 6px; }
    .grid th { background: #dce6f1; font-size: 10px; }
    .box { border: 1px solid #999; min-height: 56px; padding: 6px; font-size: 11px; margin-bottom: 8px; white-space: pre-wrap; }
    .print-hint { text-align:center; margin-top: 20px; }
    @media print { .print-hint { display: none; } body { margin: 10mm; } }
  </style></head><body>
  <div class="company">
    <img src="${await getLogoDataUrl()}" alt="Himan Logo" style="height:64px;width:64px;object-fit:contain;" />
    <h1>HIMAN INVESTMENT &amp; COMPANY</h1>
    <p>Sokoni 1, P.O.BOX 13954, Arusha</p>
    <h2>${esc(cfg.formTitle)}</h2>
  </div>
  <table class="doc"><tr><th>Document No.</th><th>Revision No.</th><th>Effective Date</th></tr>
  <tr><td>${esc(cfg.docNo)}</td><td>${esc(record.revision_no || '')}</td><td>${esc(record.effective_date || '')}</td></tr></table>
  <table class="fields">${fieldRow(cfg.refField.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), refValue)}</table>
  ${body}
  <div class="print-hint"><button onclick="window.print()">Print</button></div>
  </body></html>`;

  win.document.write(html);
  win.document.close();
  setTimeout(() => win.print(), 400);
}

/* ------------------------------------------------------------------ */
/* Generic register (table + create/edit dialog)                       */
/* ------------------------------------------------------------------ */

function emptyForm(cfg) {
  const form = {};
  for (const s of cfg.sections) for (const [key, , type] of s.fields) form[key] = type === 'readings' ? [] : '';
  return form;
}

function Register({ cfg, canDelete }) {
  const [records, setRecords] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm(cfg));

  const fetchRecords = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/quality/${cfg.entity}`, authHeaders());
      setRecords(res.data);
    } catch {
      toast.error(`Failed to load ${cfg.title}`);
    }
  }, [cfg]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const openNew = () => { setEditing(null); setForm(emptyForm(cfg)); setOpen(true); };
  const openEdit = (rec) => { setEditing(rec); setForm({ ...emptyForm(cfg), ...rec }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/quality/${cfg.entity}/${editing.id}`, form, authHeaders());
        toast.success('Record updated');
      } else {
        await axios.post(`${API}/quality/${cfg.entity}`, form, authHeaders());
        toast.success('Record created');
      }
      setOpen(false);
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (rec) => {
    if (!window.confirm(`Delete ${rec[cfg.refField] || 'this record'}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/quality/${cfg.entity}/${rec.id}`, authHeaders());
      toast.success('Record deleted');
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const addReading = () => setField('readings', [...(form.readings || []), { test_point: '', standard_value: '', instrument_reading: '', within_tolerance: '' }]);
  const setReading = (i, key, value) => {
    const rows = [...(form.readings || [])];
    rows[i] = { ...rows[i], [key]: value };
    setField('readings', rows);
  };
  const removeReading = (i) => setField('readings', (form.readings || []).filter((_, j) => j !== i));

  const statusBadge = (v) => {
    const closed = String(v).toLowerCase() === 'closed';
    return <Badge className={closed ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>{v || 'open'}</Badge>;
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">{cfg.title} <span className="text-xs font-normal text-gray-500 ml-2">Doc No. {cfg.docNo}</span></CardTitle>
        <Button onClick={openNew} data-testid={`quality-${cfg.entity}-new`}><Plus size={16} className="mr-1" /> New</Button>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {cfg.listColumns.map(([key, label]) => <TableHead key={key}>{label}</TableHead>)}
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.length === 0 && (
                <TableRow><TableCell colSpan={cfg.listColumns.length + 1} className="text-center text-gray-400">No records yet</TableCell></TableRow>
              )}
              {records.map((rec) => (
                <TableRow key={rec.id}>
                  {cfg.listColumns.map(([key]) => (
                    <TableCell key={key}>{key === 'status' ? statusBadge(rec[key]) : String(rec[key] ?? '')}</TableCell>
                  ))}
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(rec)} title="Edit"><Pencil size={14} /></Button>
                      <Button size="sm" variant="outline" onClick={() => printRecord(cfg, rec)} title="Print official form"><Printer size={14} /></Button>
                      {canDelete && (
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(rec)} title="Delete"><Trash2 size={14} /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing[cfg.refField] || ''}` : `New — ${cfg.formTitle}`}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            {!editing && (
              <p className="text-xs text-gray-500">The reference number is assigned automatically on save.</p>
            )}
            {cfg.sections.map((section, si) => (
              <div key={si} className="space-y-3">
                {section.title && <h3 className="text-sm font-semibold text-[#EA5829] border-b pb-1">{section.title}</h3>}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {section.fields.map(([key, label, type, options]) => (
                    <div key={key} className={type === 'textarea' || type === 'readings' ? 'md:col-span-2 space-y-1' : 'space-y-1'}>
                      <Label htmlFor={`f-${key}`}>{label}</Label>
                      {type === 'textarea' ? (
                        <Textarea id={`f-${key}`} rows={3} value={form[key] || ''} onChange={(e) => setField(key, e.target.value)} />
                      ) : type === 'select' ? (
                        <select id={`f-${key}`} className="w-full border rounded-md h-9 px-2 text-sm bg-white"
                          value={form[key] || ''} onChange={(e) => setField(key, e.target.value)}>
                          <option value="">— select —</option>
                          {options.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : type === 'readings' ? (
                        <div className="space-y-2">
                          {(form.readings || []).map((row, i) => (
                            <div key={i} className="grid grid-cols-9 gap-2 items-center">
                              <Input className="col-span-2" placeholder="Test point" value={row.test_point}
                                onChange={(e) => setReading(i, 'test_point', e.target.value)} />
                              <Input className="col-span-2" placeholder="Standard value" value={row.standard_value}
                                onChange={(e) => setReading(i, 'standard_value', e.target.value)} />
                              <Input className="col-span-2" placeholder="Instrument reading" value={row.instrument_reading}
                                onChange={(e) => setReading(i, 'instrument_reading', e.target.value)} />
                              <select className="col-span-2 border rounded-md h-9 px-2 text-sm bg-white" value={row.within_tolerance}
                                onChange={(e) => setReading(i, 'within_tolerance', e.target.value)}>
                                <option value="">Tol?</option><option value="Y">Y</option><option value="N">N</option>
                              </select>
                              <Button type="button" size="sm" variant="outline" className="text-red-600" onClick={() => removeReading(i)}>×</Button>
                            </div>
                          ))}
                          <Button type="button" size="sm" variant="outline" onClick={addReading}>+ Add reading</Button>
                        </div>
                      ) : (
                        <Input id={`f-${key}`} type={type} value={form[key] || ''} onChange={(e) => setField(key, e.target.value)} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <Button type="submit" className="w-full" data-testid={`quality-${cfg.entity}-save`}>
              {editing ? 'Save Changes' : 'Create Record'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Batch traceability lookup (QP-TR-01)                                */
/* ------------------------------------------------------------------ */

function BatchTrace() {
  const [batchNo, setBatchNo] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quality-batch-lookup/${encodeURIComponent(batchNo.trim())}`, authHeaders());
      setResult(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Batch Traceability <span className="text-xs font-normal text-gray-500 ml-2">Doc No. QP-TR-01 — batch format CK-DDMMYY</span></CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={lookup} className="flex gap-2">
          <Input placeholder="e.g. CK-140726" value={batchNo} onChange={(e) => setBatchNo(e.target.value)} required data-testid="batch-trace-input" />
          <Button type="submit" disabled={loading} data-testid="batch-trace-submit"><Search size={16} className="mr-1" /> Trace</Button>
        </form>
        {result && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card><CardContent className="pt-4"><p className="text-xs text-gray-500">Batch</p><p className="font-bold">{result.batch_number}</p></CardContent></Card>
              <Card><CardContent className="pt-4"><p className="text-xs text-gray-500">Cartons Produced</p><p className="font-bold">{result.carton_count}</p></CardContent></Card>
              <Card><CardContent className="pt-4"><p className="text-xs text-gray-500">Cartons Scanned/Sold</p><p className="font-bold">{result.cartons_scanned.length}</p></CardContent></Card>
              <Card><CardContent className="pt-4"><p className="text-xs text-gray-500">Complaints / Recalls</p><p className="font-bold">{result.complaints.length} / {result.recalls.length}</p></CardContent></Card>
            </div>
            {result.batch ? (
              <p>Product: <b>{result.batch.product_name}</b> · Produced {result.batch.production_date} · {result.batch.remaining_quantity}/{result.batch.initial_quantity} remaining · status {result.batch.status}</p>
            ) : (
              <p className="text-amber-600">No QR batch found with this number — check the batch code.</p>
            )}
            {result.cartons_scanned.length > 0 && (
              <div>
                <h4 className="font-semibold mb-1">Cartons scanned (forward traceability)</h4>
                <div className="overflow-x-auto"><Table>
                  <TableHeader><TableRow><TableHead>Carton</TableHead><TableHead>Customer</TableHead><TableHead>Status</TableHead><TableHead>Last Scan</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {result.cartons_scanned.map((c) => (
                      <TableRow key={c.carton_id}><TableCell>{c.carton_id}</TableCell><TableCell>{c.customer_name || '—'}</TableCell><TableCell>{c.status}</TableCell><TableCell>{(c.last_scanned_at || '').slice(0, 10)}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table></div>
              </div>
            )}
            {result.delivery_notes.length > 0 && (
              <p>{result.delivery_notes.length} delivery note(s) reference this batch.</p>
            )}
            {(result.complaints.length > 0 || result.rejected_materials.length > 0) && (
              <p className="text-red-600 font-semibold">
                ⚠ This batch has {result.complaints.length} complaint(s) and {result.rejected_materials.length} rejection record(s) — review before further dispatch.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */

export default function Quality({ user }) {
  const canDelete = user?.role?.toLowerCase() === 'admin';
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Quality Management (TBS)</h1>
        <p className="text-sm text-gray-500">
          Digital registers for the company's TBS quality-system documents — fill records here and print them on the official form layout.
        </p>
      </div>
      <Tabs defaultValue="complaints">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="complaints">Complaints</TabsTrigger>
          <TabsTrigger value="recalls">Recalls</TabsTrigger>
          <TabsTrigger value="maintenance">Maintenance</TabsTrigger>
          <TabsTrigger value="calibration">Calibration</TabsTrigger>
          <TabsTrigger value="rejected">Rejected Material</TabsTrigger>
          <TabsTrigger value="car">Corrective Actions</TabsTrigger>
          <TabsTrigger value="trace">Batch Trace</TabsTrigger>
        </TabsList>
        <TabsContent value="complaints"><Register cfg={REGISTERS.complaints} canDelete={canDelete} /></TabsContent>
        <TabsContent value="recalls"><Register cfg={REGISTERS.recalls} canDelete={canDelete} /></TabsContent>
        <TabsContent value="maintenance"><Register cfg={REGISTERS['maintenance-reports']} canDelete={canDelete} /></TabsContent>
        <TabsContent value="calibration"><Register cfg={REGISTERS['calibration-records']} canDelete={canDelete} /></TabsContent>
        <TabsContent value="rejected"><Register cfg={REGISTERS['rejected-materials']} canDelete={canDelete} /></TabsContent>
        <TabsContent value="car"><Register cfg={REGISTERS['corrective-actions']} canDelete={canDelete} /></TabsContent>
        <TabsContent value="trace"><BatchTrace /></TabsContent>
      </Tabs>
    </div>
  );
}
